import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import theme from './theme';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Events from './pages/Events';
import Players from './pages/Players';
import Gallery from './pages/Gallery';
import Sponsors from './pages/Sponsors';
import Contact from './pages/Contact';
import Shop from './pages/Shop';
import Tournaments from './pages/Tournaments';
import Loading from './components/Loading';
import Admin from './pages/Admin';
import AdminAnalytics from './pages/AdminAnalytics';
import ScrollToTop from './ScrollToTop';
import Auth from './pages/Auth';
import Dashboard from './pages/dashboard';
import MAMC from './pages/MAMC';
import VolunteerScores from './pages/VolunteerScores';
import AnalyticsTracker from './components/AnalyticsTracker';
import CartDrawer from './components/CartDrawer';
import { UserProvider, useUser } from './UserContext';
import { CartProvider } from './contexts/CartContext';
import { isTokenValid } from './utils/tokenUtils';

const AdminRoute = ({ children }) => {
  const { userData } = useUser();
  return isTokenValid() && userData?.role === 'admin' ? children : <Navigate to="/dashboard" />;
};

const UserRoute = ({ children }) => {
  const { userData } = useUser();
  return isTokenValid() && (userData?.role === 'admin' || userData?.role === 'user') ? (
    children
  ) : (
    <Navigate to="/volunteer/scores" />
  );
};

const VolunteerRoute = ({ children }) => {
  const { userData } = useUser();
  return isTokenValid() && (userData?.role === 'admin' || userData?.role === 'volunteer') ? (
    children
  ) : (
    <Navigate to="/dashboard" />
  );
};

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500); // Simulate a loading time of 3 seconds
    return () => clearTimeout(timer);
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <UserProvider>
          <CartProvider>
            <Router>
              <ScrollToTop />
              <AnalyticsTracker />
              <CartDrawer />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '100vh',
                  justifyContent: 'space-between',
                }}
              >
                <Navbar />
                {loading ? (
                  <div
                    style={{
                      width: '100vw',
                      height: 'calc(100vh - 120px)',
                      background: '#fff',
                      zIndex: 2000,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    <Loading />
                  </div>
                ) : (
                  <main style={{ flexGrow: 1 }}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/events" element={<Events />} />
                      <Route path="/players" element={<Players />} />
                      <Route path="/gallery" element={<Gallery />} />
                      <Route path="/sponsors" element={<Sponsors />} />
                      <Route path="/shop" element={<Shop />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/tournaments" element={<Tournaments />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/mamc" element={<MAMC />} />
                      <Route
                        path="/dashboard"
                        element={
                          <UserRoute>
                            <Dashboard />
                          </UserRoute>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <AdminRoute>
                            <Admin />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/analytics"
                        element={
                          <AdminRoute>
                            <AdminAnalytics />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/volunteer/scores"
                        element={
                          <VolunteerRoute>
                            <VolunteerScores />
                          </VolunteerRoute>
                        }
                      />
                    </Routes>
                  </main>
                )}
                {!loading && <Footer />}
              </div>
            </Router>
          </CartProvider>
        </UserProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
